// ============================================================================
// EMAIL CONFIG API - Frontend
// Cliente para la configuración SMTP/branding del correo (Empresa y por Sede).
// Endpoints backend (Fase 1):
//   GET  /config/email?scope=COMPANY|SITE&siteId=…
//   PUT  /config/email
//   POST /config/email/test
// La contraseña NUNCA se devuelve por la API (solo `hasPassword`).
// ============================================================================

import { api } from './client';

export type SmtpSecurity = 'NONE' | 'STARTTLS' | 'SSL';
export type EmailConfigScope = 'COMPANY' | 'SITE';

// Respuesta del GET / PUT (config sin contraseña).
export interface EmailConfig {
  scope: EmailConfigScope;
  siteId: string | null;
  exists: boolean;
  smtpHost: string;
  smtpPort: number;
  smtpSecurity: SmtpSecurity;
  smtpUser: string;
  hasPassword: boolean;
  fromName: string;
  fromEmail: string;
  subjectTemplate: string;
  headerText: string;
  footerText: string;
  isActive: boolean;
}

// Cuerpo del PUT. `smtpPassword` opcional: si se omite o va vacío, el backend
// conserva la contraseña ya guardada. `siteId` obligatorio cuando scope=SITE.
export interface UpdateEmailConfigPayload {
  scope: EmailConfigScope;
  siteId?: string;
  smtpHost: string;
  smtpPort: number;
  smtpSecurity: SmtpSecurity;
  smtpUser?: string;
  smtpPassword?: string;
  fromName: string;
  fromEmail: string;
  subjectTemplate?: string;
  headerText?: string;
  footerText?: string;
  isActive?: boolean;
}

// Cuerpo del POST /test: mismos campos + destino.
export interface TestEmailPayload extends UpdateEmailConfigPayload {
  to: string;
}

export interface TestEmailResult {
  ok: boolean;
  message: string;
}

export const emailConfigApi = {
  /**
   * Obtener la configuración de un ámbito (Empresa o una Sede concreta).
   */
  get: async (scope: EmailConfigScope, siteId?: string): Promise<EmailConfig> => {
    const params = new URLSearchParams();
    params.append('scope', scope);
    if (siteId) params.append('siteId', siteId);
    return api.get<EmailConfig>(`/config/email?${params.toString()}`);
  },

  /**
   * Guardar (crear/actualizar) la configuración de un ámbito.
   */
  update: async (payload: UpdateEmailConfigPayload): Promise<EmailConfig> => {
    return api.put<EmailConfig>('/config/email', payload);
  },

  /**
   * "Probar envío": envía un correo de prueba con los valores indicados,
   * SIN guardar. Devuelve { ok, message } (no lanza por fallo de SMTP).
   */
  test: async (payload: TestEmailPayload): Promise<TestEmailResult> => {
    return api.post<TestEmailResult>('/config/email/test', payload);
  },
};

export default emailConfigApi;
